
import React, { useState, useEffect, useMemo } from 'react';
import { Search, Trash2, X, Eye, Mail } from 'lucide-react';
import './arrondissement.css';
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../AxiosInstance";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const DemandesDevis = () => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [successMessage, setSuccessMessage] = useState('');
  const [profil, setProfil] = useState(null);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [mailModal, setMailModal] = useState(null);
  const [mailMessage, setMailMessage] = useState('');
  const [mailHtml, setMailHtml] = useState('');
  const senderName = "Wemaza Support";
  const [ccList, setCcList] = useState([]);
  const [bccList, setBccList] = useState([]);
  const [mailSubject, setMailSubject] = useState('');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const API_BASE_URL = '/api/demandes/devis';

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        setLoading(true);
        const response = await AxiosInstance.get(`${API_BASE_URL}/list`);
        
        setDemandes(response.data.data.demandes);
        setError('');
      } catch (err) {
        setError(err.message === 'Failed to fetch' ? "Vérifiez votre connexion Internet." : err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDemandes();
  }, []);

  const handleStatutChange = async (id, newStatut) => {
    try {
      await AxiosInstance.patch(`${API_BASE_URL}/${id}/statut`, { statut: newStatut });
      setDemandes(prev => prev.map(d => (d._id === id ? { ...d, statut: newStatut } : d)));
      setSuccessMessage('Statut mis à jour avec succès ✅');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (idDemande) => {
    try {
      await AxiosInstance.delete(`${API_BASE_URL}/${idDemande}`);
      setDemandes(prev => prev.filter(d => d._id !== idDemande));
      setSuccessMessage('Demande supprimée avec succès ✅');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm({ show: false, id: null });
    }
  };

  const handleSendMail = async () => {
    if (!mailModal || (!mailHtml.trim() && !mailMessage.trim())) {
      alert("Veuillez saisir un message.");
      return;
    }
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(mailHtml);
    const payload = {
      subject: mailSubject,
      htmlBody: isHtml ? mailHtml : '',
      textBody: !isHtml ? mailMessage : '',
      senderName,
      recipients: [mailModal.email],
      cc: ccList,
      bcc: bccList
    };
    try {
      await AxiosInstance.post('/api/mails/send', payload);
      setSuccessMessage('Mail envoyé avec succès ✅');
      setMailModal(null);
      setMailSubject('');
      setMailMessage('');
      setMailHtml('');
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi du mail.");
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'NON_TRAITE': return 'bg-red-200';
      case 'EN_COURS': return 'bg-yellow-200';
      case 'TRAITE': return 'bg-green-200';
      default: return 'bg-gray-200';
    }
  };

  const filteredDemandes = useMemo(() =>
    demandes.filter(d =>
      d.nomDemarche?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.telephone?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [demandes, searchTerm]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDemandes = filteredDemandes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDemandes.length / itemsPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 1500);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="arrondissement-container">
      <div className="container mx-auto px-4 py-4">
        <div className="card page-header">
          <h1 className="header-title">Gestion des Demandes de devis</h1>
          <p className="header-subtitle">Gérer les demandes de devis</p>
        </div>

        <div className="card filters-section">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher une demande, description, nom, contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="table-container">
          <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <label>Afficher</label>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
              <span>éléments</span>
            </div>

            <div>
              {filteredDemandes.length > 0 && (
                <span>
                  Affichage {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredDemandes.length)} sur {filteredDemandes.length}
                </span>
              )}
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead className="table-header">
                <tr>
                  <th>Categorie de service</th>
                  <th>Description</th>
                  <th>Nom du client</th>
                  <th>Contact</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {currentDemandes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      <p className="empty-title">Aucune demande trouvée</p>
                      <p className="empty-subtitle">Essayez de modifier vos critères de recherche</p>
                    </td>
                  </tr>
                ) : currentDemandes.map(demande => (
                  <tr key={demande._id} className="table-row">
                    <td>{demande.nomDemarche}</td>
                    <td>{demande.description || 'Aucune description'}</td>
                    <td>{`${demande.nom || ''} ${demande.prenom || ''}`.trim() || 'Non précisé'}</td>
                    <td>{demande.telephone || 'Non précisé'}</td>
                    <td className={`cell-statut ${getStatutColor(demande.statut)}`}>{demande.statut}</td>
                    <td>
                      <div className="actions-container">
                        <select
                          value={demande.statut}
                          onChange={(e) => handleStatutChange(demande._id, e.target.value)}
                          className="statut-select"
                        >
                          <option value="NON_TRAITE">Non Traitée</option>
                          <option value="EN_COURS">En Cours</option>
                          <option value="TRAITE">Traitée</option>
                        </select>
                        <button
                          className="action-button info"
                          onClick={() => setSelectedDemande(demande)}
                          title="Voir récapitulatif"
                        >
                          <Eye className="action-icon eye" />
                        </button>
                        <button
                          className="action-button info"
                          onClick={() => setMailModal(demande)}
                          title="Envoyer un mail"
                        >
                          <Mail className="action-icon mail" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ show: true, id: demande._id })}
                          className="action-button delete"
                          title="Supprimer"
                        >
                          <Trash2 className="action-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="pagination-container">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            Précédent
          </button>

          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => setCurrentPage(number)}
              className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
            >
              {number}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            Suivant
          </button>
        </div>

        <div className="card stats-section">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number blue">{demandes.length}</div>
              <div className="stat-label">Total Demandes de devis</div>
            </div>
          </div>
        </div>
      </div>
      {mailModal && (
        <div className="mail-modal-overlay">
          <div className="mail-modal-content">
            <div className="mail-modal-header">
              <h2 className="mail-modal-title">Nouveau message</h2>
              <button onClick={() => setMailModal(null)} className="close-btn"><X /></button>
            </div>
            <div className="mail-modal-body">
              <div className="mail-modal-input-group">
                <label htmlFor="emailTo">À</label>
                <input
                  id="emailTo"
                  type="email"
                  placeholder="Entrez l'email"
                  value={mailModal.email || ''}
                  onChange={(e) => setMailModal({ ...mailModal, email: e.target.value })}
                  className="mail-modal-input"
                />
              </div>
              <div className="mail-modal-input-group">
                <label htmlFor="cc">Cc</label>
                <input
                  id="cc"
                  type="text"
                  placeholder="Ajoutez des CC (facultatif)"
                  value={ccList.join(', ')}
                  onChange={(e) => setCcList(e.target.value.split(','))}
                  className="mail-modal-input"
                />
              </div>

              <div className="mail-modal-input-group">
                <label htmlFor="subject">Objet</label>
                <input
                  id="subject"
                  type="text"
                  placeholder="Entrez l'objet du message"
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  className="mail-modal-input"
                />
              </div>

              <div className="mail-modal-input-group mail-modal-quill-group">
                <label htmlFor="message">Message</label>
                <ReactQuill
                  value={mailHtml}
                  onChange={setMailHtml}
                  className="mail-modal-quill"
                  placeholder="Entrez votre message ici..."
                  modules={{
                    toolbar: [
                      [{ 'font': [] }, { 'size': [] }],
                      [{ 'align': [] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link', 'image', 'video'],
                      [{ 'color': [] }, { 'background': [] }],
                      ['blockquote', 'code-block'],
                      [{ 'indent': '-1' }, { 'indent': '+1' }],
                      [{ 'direction': 'rtl' }],
                      ['clean'],
                    ],
                  }}
                />
              </div>

            </div>

            <div className="mail-modal-footer">
              <button
                className="mail-modal-btn cancel"
                onClick={() => {
                  setMailModal(null);
                  setMailSubject('');
                  setMailMessage('');
                  setMailHtml('');
                }}
              >
                Annuler
              </button>
              <button className="mail-modal-btn send" onClick={handleSendMail}>Envoyer</button>
            </div>
          </div>
        </div>
      )}
      {selectedDemande && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h2 className="modal-title">Récapitulatif de la demande</h2>
              <button onClick={() => setSelectedDemande(null)}><X /></button>
            </div>

            <div className="modal-body">
              {/* Menu 1 : Informations du client */}
              <div className="recap-section">
                <h3>Informations du client</h3>
                <p>
                  <strong>Nom complet :</strong>{' '}
                  {`${selectedDemande.nom || ''} ${selectedDemande.prenom || ''}`.trim() || 'Non précisé'}
                </p>
                <p><strong>Email :</strong> {selectedDemande.email || 'Non précisé'}</p>
                <p><strong>Téléphone :</strong> {selectedDemande.telephone || 'Non précisé'}</p>
                <p><strong>Adresse :</strong> {selectedDemande.adresse || 'Non précisée'}</p>
              </div>

              {/* Menu 2 : Type de service demandé */}
              <div className="recap-section">
                <h3>Type de service demandé</h3>
                <p><strong>Catégorie :</strong> {selectedDemande.nomDemarche}</p>
                <p><strong>Description :</strong> {selectedDemande.description || 'Aucune'}</p>
              </div>

              {/* Menu 3 : Délai et urgence */}
              <div className="recap-section">
                <h3>Délai et urgence</h3>
                <p><strong>Date souhaitée de début :</strong> {selectedDemande.date_debut || 'Non précisée'}</p>
                <p><strong>Niveau d’urgence :</strong> {selectedDemande.urgence || 'Non précisé'}</p>
              </div>
              <div className="recap-section">
                <h3>Informations complementaires</h3>
                <p><strong> D’où le client connaît nos services :</strong> {selectedDemande.source || 'Non précisée'}</p>
                <p><strong>Remarques ou informations supplementaires:</strong> {selectedDemande.remarques || 'Non précisé'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="modal-overlay">
          <div className="modal-content small">
            <div className="modal-header"><h2>Succès</h2></div>
            <div className="modal-body"><p>{successMessage}</p></div>
          </div>
        </div>
      )}
      {deleteConfirm.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Confirmation</h2>
              <button onClick={() => setDeleteConfirm({ show: false, id: null })}>
                <X />
              </button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous vraiment supprimer cette demande d'ajout ?</p>
              <div className="modal-actions">
                <button
                  className="btn btn-danger"
                  onClick={() => setDeleteConfirm({ show: false, id: null })}
                >Non</button>
                <button
                  className="btn btn-primary"
                  onClick={() => handleDelete(deleteConfirm.id)}
                >Oui</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DemandesDevis;
